/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type { FC, ReactNode, SVGProps } from 'react';

import { useClickOutside } from '../../hooks/useClickOutside';
import { useTrapFocus } from '../../hooks/useTrapFocus';

import DropDownArrow from '@icons/DropDownArrow.svg';

type Provider = {
  type: string;
  title: string;
  username?: string;
  icon: FC<SVGProps<SVGElement>>;
};

export interface Props {
  /**
   * Whether the selector dropwdown is displayed
   */
  active?: boolean;
  /**
   * ClassName to be applied to the component
   */
  className?: string;
  /**
   * ClassName to be applied to the selector button
   */
  buttonClassName?: string;
  /**
   * Children to be rendered inside the selector dropdown
   */
  children?: ReactNode;
  /**
   * Content to be displayed when no providers are available
   */
  emptyProvider: Provider;
  /**
   * Content to be displayed when providers are synced
   */
  syncedProvider: Provider;
  /**
   * Providers to list in the selector dropdown
   */
  providers?: Provider[];
  /**
   * Selected default provider
   */
  selectedProvider?: string;
  /**
   * Whether the providers are synced
   */
  synced: boolean;
  /**
   * Custom callback when an option's onChange event is fired
   */
  onChange: (value: string) => void;
  /**
   * Callback to be called on the open/close events
   */
  onToggle?: (value: boolean) => void;
}

export const ProviderSelector = ({
  active,
  className,
  buttonClassName,
  children,
  emptyProvider,
  syncedProvider,
  providers,
  selectedProvider,
  synced,
  onChange,
  onToggle,
}: Props) => {
  const wrapperRef = useClickOutside({
    callback: () => {
      toggleCallback(false);
    },
    parent: document.getElementById('scrollContainer'),
  });

  const defaultProvider = providers?.find((x) => x.type === selectedProvider);
  const otherProviders = !synced ? providers?.filter((x) => x.type !== selectedProvider) : undefined;

  const getSelectedContent = (): Provider => {
    if (synced) return syncedProvider;

    if (defaultProvider) {
      return {
        type: defaultProvider.type,
        title: defaultProvider.title,
        username: defaultProvider.username,
        icon: defaultProvider.icon,
      };
    }

    return emptyProvider;
  };

  const selectedContent = getSelectedContent();

  const hasDropdown = (!!otherProviders && otherProviders.length > 0) || !!children;

  const toggleCallback = (active: boolean) => {
    if (onToggle) {
      onToggle(active);
    }
  };

  const handleProviderChange = (type: string) => {
    onChange(type);
  };

  return (
    <div
      ref={wrapperRef}
      className={classNames('relative', 'flex flex-col', 'font-sans text-neutral-text-light', className)}
    >
      <button
        className={classNames(
          'flex items-center justify-between gap-2',
          'h-10 min-w-24',
          'p-2',
          'rounded-full',
          'border border-white/12',
          'text-md',
          'transition-all duration-300',
          {
            'bg-surface-black': !active,
            'bg-surface-dark': active,
            'cursor-pointer': onToggle,
            'hover:bg-surface-dark': onToggle && !active,
            'focus-visible:outline-offset-0 focus-visible:outline-white': onToggle,
          },
          buttonClassName,
        )}
        title={selectedContent.title}
        onClick={() => {
          toggleCallback(!active);
        }}
        data-cy='provider-selector-btn'
      >
        <selectedContent.icon className='size-6 shrink-0' />
        <span className='w-full overflow-hidden text-left text-ellipsis whitespace-nowrap'>
          {selectedContent.username}
        </span>
        {hasDropdown && (
          <DropDownArrow
            className={classNames('size-4 shrink-0', 'transform transition-transform', { 'rotate-180': active })}
          />
        )}
      </button>
      {hasDropdown && (
        <div className='absolute top-12 z-0 w-max' aria-hidden={!active}>
          <AnimatePresence>
            {active && (
              <ProviderSelectorContent providers={otherProviders} onChange={handleProviderChange}>
                {children}
              </ProviderSelectorContent>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

const ProviderSelectorContent = ({
  children,
  providers,
  onChange,
}: {
  children: Props['children'];
  providers: Props['providers'];
  onChange: Props['onChange'];
}) => {
  const dropdownRef = useTrapFocus();

  return (
    <motion.div
      ref={dropdownRef}
      initial={{ opacity: 0, translateY: '-1.875rem' }}
      animate={{ opacity: 1, translateY: '0rem' }}
      exit={{ opacity: 0, translateY: '-1.875rem' }}
      transition={{
        duration: 0.3,
      }}
      className={classNames(
        'flex flex-col gap-3',
        'p-3',
        'rounded-sm',
        'border border-white/12',
        'bg-surface-dark',
        'text-sm font-semibold',
        'shadow-lg',
      )}
    >
      {providers?.map(({ type, title, username, icon: Icon }) => (
        <button
          key={`providers-${type}`}
          onClick={() => {
            onChange(type);
          }}
          className={classNames(
            'flex shrink items-center justify-start gap-2',
            'cursor-pointer',
            'transition-colors duration-300',
            'hover:text-neutral-text-xlight',
            'focus-visible:bg-surface-dark focus-visible:outline-offset-2',
          )}
          title={title}
          data-cy={`provider-${type}-btn`}
        >
          <Icon className='pointer-events-none relative h-5 w-5 shrink-0 text-neutral-text-medium' />
          <span className='w-full overflow-hidden text-left text-ellipsis whitespace-nowrap'>{username || title}</span>
        </button>
      ))}
      {children}
    </motion.div>
  );
};
